import {
    AIModelType,
    classifyChecklistWriteError,
    ClauseChecklistRefreshUnconfirmedError,
    IClauseChecklist,
    IClauseChecklistCreate,
    IOrganization,
    useCreateClauseChecklist,
} from "@/api";
import { DialogFormContainer } from "@/components/shared/dialogs/DialogFormContainer";
import {
    Dialog,
    DialogContent,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { FormContainer } from "@rhf-kit/mui";
import { enqueueSnackbar } from "notistack";
import { DialogProps } from "@toolpad/core/useDialogs";
import { useState } from "react";
import { useNavigate } from "react-router";
import {
    ClauseChecklistForm,
    IClauseChecklistFormValues,
} from "./ClauseChecklistForm";
import { ClauseChecklistMutationError } from "./ClauseChecklistMutationError";
import { ClauseChecklistMutationPending } from "./ClauseChecklistMutationPending";
import { ClauseChecklistMutationSuccess } from "./ClauseChecklistMutationSuccess";

interface IProps {
    /**
     * The organization to create the clause checklist for
     */
    organization?: IOrganization;
}

const FORM_REFUSED =
    "The checklist couldn't be saved. Check the details and try again.";
const CREATE_UNCONFIRMED =
    "The checklist was created, but some views couldn't refresh.";

/**
 * Dialog to add a new clause checklist.
 */
export const NewClauseChecklistDialog = ({
    open,
    onClose,
    payload,
}: DialogProps<IProps>) => {
    const { organization } = payload;

    const navigate = useNavigate();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const [refusedGroupId, setRefusedGroupId] = useState<number | null>(null);
    const [failure, setFailure] = useState<string | null>(null);

    const { mutate, isPending, isSuccess, error } = useCreateClauseChecklist();
    const keepsForm =
        !error || classifyChecklistWriteError(error).kind === "validation";

    const handleSuccess = (formData: IClauseChecklistFormValues) => {
        if (!formData.organization && formData.organization_group_id === null) {
            return;
        }

        const clauseChecklistCreate: IClauseChecklistCreate = {
            name: formData.name,
            description: formData.description,
            clause_ids: formData.clauses,
            organization_ids: formData.organization
                ? [formData.organization.id]
                : [],
            model_type: formData.modelType,
            show_gaps: formData.show_gaps,
        };
        if (formData.organization_group_id !== null) {
            clauseChecklistCreate.organization_group_id =
                formData.organization_group_id;
        }

        setFailure(null);
        setRefusedGroupId(null);
        const submittedGroupId = formData.organization_group_id;
        mutate(clauseChecklistCreate, {
            onSuccess: (clauseChecklist: IClauseChecklist) => {
                navigate(`/admin/checklists/${clauseChecklist.id}/details`);
                onClose();
            },
            onError: (mutationError) => {
                if (
                    mutationError instanceof
                    ClauseChecklistRefreshUnconfirmedError
                ) {
                    enqueueSnackbar(CREATE_UNCONFIRMED, { variant: "warning" });
                    navigate(
                        `/admin/checklists/${mutationError.confirmedChecklistId}/details`
                    );
                    onClose();
                    return;
                }
                const failure = classifyChecklistWriteError(mutationError, {
                    organization_group_id: submittedGroupId,
                });
                if (failure.kind === "group_refused") {
                    setRefusedGroupId(failure.targetId);
                } else if (
                    failure.kind === "validation" &&
                    (failure.status === 422 || failure.detail === null)
                ) {
                    setFailure(FORM_REFUSED);
                }
            },
        });
    };

    const defaultValues: IClauseChecklistFormValues = {
        name: "",
        description: "",
        clauses: [],
        organization: organization ?? null,
        organization_group_id: null,
        modelType: AIModelType.CHATGPT,
        show_gaps: true,
    };

    const renderForm = () => (
        <>
            {/* Show loading state */}
            {isPending && <ClauseChecklistMutationPending />}

            <Stack alignItems={"center"}>
                <Typography variant="h6" fontWeight={600}>
                    New Checklist
                </Typography>
                {!isPending && (
                    <Typography variant={"body2"} color={"textSecondary"}>
                        Please provide the following information.
                    </Typography>
                )}
            </Stack>
            {!isPending && (
                <ClauseChecklistForm
                    onClose={onClose}
                    error={error}
                    refusedGroupId={refusedGroupId}
                    failure={failure}
                />
            )}
        </>
    );

    const renderSuccess = () => (
        <ClauseChecklistMutationSuccess onClose={onClose} />
    );

    const renderError = () => (
        <ClauseChecklistMutationError onClose={onClose} />
    );

    return (
        <Dialog
            open={open}
            onClose={() => onClose()}
            maxWidth={"lg"}
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    maxHeight: isMobile ? "auto" : "800px",
                    height: isMobile ? "100%" : "90%",
                },
            }}
        >
            <DialogContent sx={{ display: "flex", flexDirection: "column" }}>
                <DialogFormContainer>
                    <FormContainer
                        defaultValues={defaultValues}
                        onSuccess={handleSuccess}
                    >
                        <Stack
                            alignItems={"center"}
                            gap={4}
                            width={"100%"}
                            maxWidth={600}
                            marginX={"auto"}
                        >
                            {/* Initial State */}
                            {!isSuccess && keepsForm && renderForm()}

                            {/* Success State */}
                            {isSuccess && renderSuccess()}

                            {/* Error State */}
                            {!keepsForm && renderError()}
                        </Stack>
                    </FormContainer>
                </DialogFormContainer>
            </DialogContent>
        </Dialog>
    );
};
